/**
 * The App Gallery — every application in the portal, in one list, with the
 * few things an admin manages ABOUT an app rather than IN it: whether it
 * shows on the home page, its thumbnail, a lock against accidental
 * deletion, and sharing.
 *
 * Deliberately thin. Edit still opens the real Builder — this is not a
 * second place to design a map — and the fields toggled here go through
 * `moduleService.setGalleryFields`'s own narrow endpoint rather than the
 * general save path, because the list this page renders holds only a
 * SUMMARY of each module (see that function's own comment): loading the
 * full document just to flip one flag would be the exact waste the summary
 * exists to avoid.
 *
 * Every per-app action lives behind ONE 3-dot menu (reusing the same
 * `LayerOptionsMenu` the layer list's own 3-dot button uses) rather than a
 * row of icon buttons — the row/card has to hold its own weight in both
 * view modes below, and a rack of five buttons does not survive being
 * squeezed onto a grid card the way one button does.
 *
 * Any signed-in user reaches this tab now (see SettingsPage), and `modules`
 * already comes in pre-scoped to what that user may see (their own + shared
 * + group-granted — same list the portal home page uses). Per-row actions
 * are gated further, per module, by `getModuleAccess`: an owner (or admin)
 * gets the full menu, someone a module was only shared WITH gets a
 * view-only "Open" instead of Edit/Delete/Lock/Thumbnail, and "Share…" shows
 * for an owner only when the caller's role holds `modules:share` (passed in
 * as `canShareModules`), or always for an admin — see that function's own
 * rules.
 */

import { useMemo, useRef, useState } from 'react';
import {
  ArrowLeft,
  Building2,
  Check,
  ChevronDown,
  ChevronUp,
  ImageOff,
  ImagePlus,
  LayoutGrid,
  List,
  Lock,
  LockOpen,
  MoreVertical,
  Pencil,
  Search,
  Share2,
  Trash2,
} from 'lucide-react';
import type { AppModule } from '../../types';
import { useAuth, usePermissions } from '../../auth/AuthContext';
import { getModuleAccess } from '../../auth/modulePermissions';
import { moduleService } from '../../services/moduleService';
import { isDashboard } from '../../dashboard/services/dashboardService';
import { moveModule, sortByHomeOrder } from '../../utils/moduleOrder';
import ImagePicker from './ImagePicker';
import LayerOptionsMenu, { type LayerMenuItem } from '../layers/LayerOptionsMenu';

type ViewMode = 'list' | 'grid';

export default function AppGalleryTab({
  modules,
  onOpen,
  onEdit,
  onShare,
  onDelete,
  onModulesChanged,
  ownerName,
  sharedWithNames,
  canShareModules = false,
}: {
  modules: AppModule[];
  /** Opens a module however is appropriate for what the caller can do with
   *  it — the read-only viewer when they cannot edit it, same as the home
   *  page's own card click. Used wherever this file would otherwise call
   *  `onEdit` for someone WITHOUT edit access (view-only "Open"); falls
   *  back to `onEdit` itself if omitted, so nothing breaks for a caller
   *  that has not been updated to pass it. */
  onOpen?: (id: string) => void;
  onEdit: (id: string) => void;
  onShare: (id: string) => void;
  onDelete: (id: string) => void;
  onModulesChanged: (modules: AppModule[]) => void;
  /** This app's owner, to show under its title — `null` suppresses the line
   *  entirely for one module (My Space's own function returns `null` for a
   *  row the viewer owns, since naming them on their own app is noise; a row
   *  only on My Space because it was SHARED with them returns a name same as
   *  Organization always does). Labelled "Owned by"/"Shared by" here based on
   *  `getModuleAccess`'s own `isOwner` flag — the same source of truth the
   *  3-dot menu's gating already uses. */
  ownerName?: (module: AppModule) => string | null;
  /** Names of every user this app is directly shared with — omitted/empty
   *  draws nothing, so an app nobody has shared reads exactly as it did
   *  before this prop existed. */
  sharedWithNames?: (module: AppModule) => string[];
  /** Whether the CALLER's own role holds `modules:share` — threaded into
   *  `getModuleAccess` so a permitted owner (not just admin) sees "Share…". */
  canShareModules?: boolean;
}) {
  const { currentUser } = useAuth();
  const { isAdmin } = usePermissions();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  /** The one app whose "Application information" detail is open — a click
   *  on the row itself, entirely separate from the 3-dot menu beside it. */
  const [selectedId, setSelectedId] = useState<string | null>(null);
  /** The one app whose thumbnail picker is open — one at a time, so this
   *  never turns into a page of simultaneously open forms. */
  const [thumbnailTargetId, setThumbnailTargetId] = useState<string | null>(null);
  /** The one app whose 3-dot menu is open. */
  const [menuTargetId, setMenuTargetId] = useState<string | null>(null);
  const menuAnchorRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  // The App Gallery's own order IS the home page's order (see
  // utils/moduleOrder.ts) — shown in this order here so "top to bottom in
  // the gallery" and "left to right, top to bottom on the home page" are
  // visibly the same list, not two things an admin has to trust line up.
  const ordered = useMemo(() => sortByHomeOrder(modules), [modules]);

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return ordered;
    return ordered.filter((module) => (module.title || module.name || '').toLowerCase().includes(term));
  }, [ordered, query]);

  /**
   * Move up/down only make sense against the FULL, unfiltered order — moving
   * "up" within a search result would silently reorder against neighbours
   * the admin can't currently see. Disabled (see the buttons below) whenever
   * a search is active, rather than operating on a list that disagrees with
   * what is on screen.
   */
  const reorder = async (module: AppModule, direction: 'up' | 'down') => {
    const changes = moveModule(ordered, module.id, direction);
    if (!changes) return;

    setBusyId(module.id);
    setError(null);
    try {
      let next = modules;
      for (const change of changes) {
        // Only the entries whose position actually changed need a write —
        // for a simple adjacent swap that is normally just the two of them,
        // not the whole gallery.
        const current = next.find((item) => item.id === change.id);
        if (!current || current.homeOrder === change.homeOrder) continue;
        next = await moduleService.setGalleryFields(currentUser, change.id, {
          homeOrder: change.homeOrder,
        });
      }
      onModulesChanged(next);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Could not reorder applications.');
    } finally {
      setBusyId(null);
    }
  };

  const thumbnailTarget = modules.find((module) => module.id === thumbnailTargetId) ?? null;

  const patch = async (
    module: AppModule,
    changes: { showOnHome?: boolean; thumbnailUrl?: string | null; locked?: boolean },
  ) => {
    setBusyId(module.id);
    setError(null);
    try {
      const next = await moduleService.setGalleryFields(currentUser, module.id, changes);
      onModulesChanged(next);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Could not update this application.');
    } finally {
      setBusyId(null);
    }
  };

  /** Owner-or-admin, same `_may_edit` rule the server enforces — unlike
   *  `onShare` (per-user, admin-only), this is the app's own version of the
   *  toggle `LayerShareDialog` already offers a layer's owner. */
  const toggleOrgShare = async (module: AppModule) => {
    setBusyId(module.id);
    setError(null);
    try {
      const next = await moduleService.setOrganizationShare(module.id, !module.sharedWithOrg);
      onModulesChanged(next);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Could not change this application's sharing.");
    } finally {
      setBusyId(null);
    }
  };

  const remove = (module: AppModule) => {
    // The menu item is already disabled for a locked app; this is the same
    // check again for anyone who reaches it another way (a stale render).
    if (module.locked) {
      setError(`"${module.title || module.name}" is locked. Unlock it before deleting.`);
      return;
    }
    if (!window.confirm(`Delete "${module.title || module.name}"? This cannot be undone.`)) return;
    onDelete(module.id);
  };

  const menuItemsFor = (module: AppModule): LayerMenuItem[] => {
    const access = getModuleAccess(currentUser, module, canShareModules);
    if (!access.canEdit) {
      // Shared-with-view-only: nothing here to manage, just a way in —
      // `onOpen` (falling back to `onEdit` only if the caller never passed
      // it) is what actually opens the read-only viewer instead of being
      // refused; see this file's own doc on `onOpen` for why `onEdit` alone
      // cannot be trusted to do that.
      return [{ id: 'open', label: 'Open', icon: Pencil, onSelect: () => (onOpen ?? onEdit)(module.id) }];
    }
    return [
      { id: 'edit', label: 'Edit', icon: Pencil, onSelect: () => onEdit(module.id) },
      ...(access.canShare
        ? [{ id: 'share', label: 'Share…', icon: Share2, onSelect: () => onShare(module.id) }]
        : []),
      // Whether an app appears on the portal's shared Home Page is a
      // portal-wide curation call, not something every owner should flip for
      // an app everyone sees — stays admin-only even for the app's own owner.
      ...(isAdmin
        ? [
            {
              id: 'home',
              label: module.showOnHome === false ? 'Show on Home Page' : 'Hide from Home Page',
              icon: module.showOnHome === false ? LayoutGrid : List,
              onSelect: () => void patch(module, { showOnHome: module.showOnHome === false }),
            },
          ]
        : []),
      {
        id: 'thumbnail',
        label: 'Change thumbnail…',
        icon: ImagePlus,
        onSelect: () => setThumbnailTargetId(module.id),
      },
      {
        id: 'lock',
        label: module.locked ? 'Unlock' : 'Lock against deletion',
        icon: module.locked ? Lock : LockOpen,
        onSelect: () => void patch(module, { locked: !module.locked }),
        separatorBefore: true,
      },
      {
        id: 'delete',
        label: 'Delete',
        icon: Trash2,
        onSelect: () => remove(module),
        disabled: module.locked || !access.canDelete,
        tooltip: module.locked ? 'Locked — unlock to delete' : undefined,
        danger: true,
      },
    ];
  };

  const Thumbnail = ({ module, className }: { module: AppModule; className: string }) =>
    module.thumbnailUrl ? (
      <img src={module.thumbnailUrl} alt="" className={`${className} object-cover`} />
    ) : (
      <div className={`${className} flex items-center justify-center bg-slate-100`}>
        <ImageOff className="h-4 w-4 text-slate-300" />
      </div>
    );

  const badges = (module: AppModule) => {
    const ownerLabel = ownerName?.(module) ?? null;
    const isOwn = getModuleAccess(currentUser, module, canShareModules).isOwner;
    return (
    <>
      <p className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-slate-400">
        <span>{isDashboard(module) ? 'Dashboard' : 'Map'}</span>
        {module.locked && (
          <span className="inline-flex items-center gap-0.5 font-medium text-amber-600">
            <Lock className="h-3 w-3" /> Locked
          </span>
        )}
        {module.showOnHome === false && <span>Hidden from home page</span>}
        {ownerLabel && <span>{isOwn ? 'Owned by' : 'Shared by'} {ownerLabel}</span>}
      </p>
      {sharedWithNames && sharedWithNames(module).length > 0 && (
        <p className="mt-0.5 flex items-center gap-1 truncate text-[11px] text-teal-700">
          <Share2 className="h-3 w-3 flex-shrink-0" />
          {(() => {
            const names = sharedWithNames(module);
            const shown = names.slice(0, 3).join(', ');
            const extra = names.length - 3;
            return <span className="truncate">Shared with {shown}{extra > 0 ? ` +${extra} more` : ''}</span>;
          })()}
        </p>
      )}
    </>
    );
  };

  const selected = selectedId ? modules.find((module) => module.id === selectedId) ?? null : null;

  // A click on the row/card opens this — entirely separate from, and
  // additive to, the existing 3-dot menu (Edit/Share/Delete etc. below stay
  // exactly as they were).
  if (selected) {
    const sharedWith = sharedWithNames?.(selected) ?? [];
    const selectedAccess = getModuleAccess(currentUser, selected, canShareModules);
    const selectedOwnerLabel = ownerName?.(selected) ?? null;
    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => setSelectedId(null)}
          className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-blue-600 hover:text-blue-800"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to all applications
        </button>

        <div className="flex items-start gap-4">
          <Thumbnail module={selected} className="h-20 w-20 flex-shrink-0 overflow-hidden rounded-lg" />
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-blue-600">
              Application information
            </p>
            <h3 className="mt-0.5 truncate text-base font-semibold text-slate-900">
              {selected.title || selected.name}
            </h3>
            {badges(selected)}
          </div>
        </div>

        <dl className="space-y-2">
          <div className="grid grid-cols-[130px_1fr] gap-3 rounded-lg bg-slate-50 px-3 py-2">
            <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Type</dt>
            <dd className="text-xs text-slate-700">{isDashboard(selected) ? 'Dashboard' : 'Map'}</dd>
          </div>
          {selectedOwnerLabel && (
            <div className="grid grid-cols-[130px_1fr] gap-3 rounded-lg bg-slate-50 px-3 py-2">
              <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                {selectedAccess.isOwner ? 'Owner' : 'Shared by'}
              </dt>
              <dd className="text-xs text-slate-700">{selectedOwnerLabel}</dd>
            </div>
          )}
          <div className="grid grid-cols-[130px_1fr] gap-3 rounded-lg bg-slate-50 px-3 py-2">
            <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Created</dt>
            <dd className="text-xs text-slate-700">
              {selected.createdAt ? new Date(selected.createdAt).toLocaleString() : 'Unknown'}
            </dd>
          </div>
          <div className="grid grid-cols-[130px_1fr] gap-3 rounded-lg bg-slate-50 px-3 py-2">
            <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Modified</dt>
            <dd className="text-xs text-slate-700">
              {selected.updatedAt ? new Date(selected.updatedAt).toLocaleString() : 'Unknown'}
            </dd>
          </div>
        </dl>

        {selectedAccess.canEdit && (
          <button
            type="button"
            onClick={() => void toggleOrgShare(selected)}
            disabled={busyId === selected.id}
            className={`flex w-full items-center gap-2.5 rounded-lg border px-3 py-2.5 text-left transition disabled:opacity-50 ${
              selected.sharedWithOrg ? 'border-indigo-300 bg-indigo-50' : 'border-slate-200 hover:bg-slate-50'
            }`}
          >
            <span
              className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border ${
                selected.sharedWithOrg ? 'border-indigo-500 bg-indigo-500' : 'border-slate-300 bg-white'
              }`}
            >
              {selected.sharedWithOrg && <Check className="h-3 w-3 text-white" />}
            </span>
            <Building2 className="h-4 w-4 flex-shrink-0 text-indigo-500" />
            <span className="min-w-0 flex-1">
              <span className="block text-xs font-medium text-[#0F2942]">Share with Organization</span>
              <span className="block text-[11px] text-slate-400">
                Every signed-in user can see this app — view only, in Settings &rsaquo; Organization.
              </span>
            </span>
          </button>
        )}

        {sharedWithNames && (
          <div className="border-t border-slate-100 pt-3">
            <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              <Share2 className="h-3.5 w-3.5" />
              Shared with
            </p>
            {sharedWith.length === 0 ? (
              <p className="text-[12px] text-slate-400">Not shared with anyone yet.</p>
            ) : (
              <ul className="flex flex-wrap gap-1.5">
                {sharedWith.map((name) => (
                  <li
                    key={name}
                    className="rounded-full bg-teal-50 px-2.5 py-0.5 text-[11.5px] font-medium text-teal-700"
                  >
                    {name}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <button
          type="button"
          onClick={() => (onOpen ?? onEdit)(selected.id)}
          className="gl-btn gl-btn--primary px-3.5 py-1.5 text-[12.5px]"
        >
          Open
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-[11.5px] text-slate-500">
        {isAdmin
          ? 'Every application in the portal — what shows on the home page, its thumbnail, sharing, and a lock against accidental deletion.'
          : 'Every application you own or have been shared. You can manage your own — thumbnail, lock, delete — but not one someone else shares with you, or an admin’s.'}
      </p>

      {/* Toolbar: search + view switch, the way a file browser puts them side by side. */}
      <div className="flex items-center gap-2">
        <label className="relative flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search applications…"
            className="w-full rounded-lg border border-slate-200 bg-slate-50 py-1.5 pl-8 pr-3 text-[12.5px] text-slate-800 outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100"
          />
        </label>
        <div className="flex flex-shrink-0 items-center gap-0.5 rounded-lg border border-slate-200 bg-white p-0.5">
          <button
            type="button"
            onClick={() => setViewMode('list')}
            aria-pressed={viewMode === 'list'}
            title="List view"
            className={`rounded-md p-1.5 transition ${viewMode === 'list' ? 'bg-slate-800 text-white' : 'text-slate-500 hover:bg-slate-100'}`}
          >
            <List className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setViewMode('grid')}
            aria-pressed={viewMode === 'grid'}
            title="Grid view"
            className={`rounded-md p-1.5 transition ${viewMode === 'grid' ? 'bg-slate-800 text-white' : 'text-slate-500 hover:bg-slate-100'}`}
          >
            <LayoutGrid className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {error && (
        <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-xs font-medium text-red-600">
          {error}
        </p>
      )}

      {/* One shared thumbnail editor rather than one per row/card — a form
          opening inside a grid card would fight the card's own size. */}
      {thumbnailTarget && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <p className="mb-2 text-[11px] font-semibold text-slate-600">
            Thumbnail for “{thumbnailTarget.title || thumbnailTarget.name}”
          </p>
          <ImagePicker
            label="Thumbnail"
            value={thumbnailTarget.thumbnailUrl}
            onChange={(url) => void patch(thumbnailTarget, { thumbnailUrl: url ?? null })}
            maxBytes={2 * 1024 * 1024}
            maxDimension={400}
            targetBytes={80 * 1024}
            previewAspect="square"
            hint="Shown on this app's card, here and on the home page."
          />
          <button
            type="button"
            onClick={() => setThumbnailTargetId(null)}
            className="mt-2 text-[11px] font-medium text-slate-500 hover:text-slate-700"
          >
            Done
          </button>
        </div>
      )}

      {modules.length === 0 ? (
        <p className="rounded-lg border border-dashed border-slate-300 px-3 py-10 text-center text-sm text-slate-400">
          No applications yet. Use “Add Module” on the home page to create one.
        </p>
      ) : filtered.length === 0 ? (
        <p className="rounded-lg border border-dashed border-slate-300 px-3 py-10 text-center text-sm text-slate-400">
          No applications match “{query}”.
        </p>
      ) : viewMode === 'list' ? (
        <ul className="space-y-2">
          {filtered.map((module) => {
            const position = ordered.findIndex((item) => item.id === module.id);
            return (
            <li key={module.id} className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2.5">
              <button
                type="button"
                onClick={() => setSelectedId(module.id)}
                className="flex min-w-0 flex-1 items-center gap-3 text-left"
              >
                <Thumbnail module={module} className="h-10 w-10 flex-shrink-0 overflow-hidden rounded-lg" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px] font-medium text-slate-800">
                    {module.title || module.name}
                  </p>
                  {badges(module)}
                </div>
              </button>
              {/* Home page order — admin-only, same reasoning as the "show
                  on home page" toggle below: this decides what every viewer
                  sees, not something every owner should tune for an app
                  everyone shares. Disabled while a search is active, since
                  "up"/"down" against a filtered list would silently reorder
                  against neighbours the admin can't currently see. */}
              {isAdmin && (
                <div className="flex flex-shrink-0 flex-col">
                  <button
                    type="button"
                    onClick={() => void reorder(module, 'up')}
                    disabled={busyId === module.id || query.trim() !== '' || position <= 0}
                    aria-label={`Move ${module.title || module.name} up`}
                    title="Move up"
                    className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30"
                  >
                    <ChevronUp className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => void reorder(module, 'down')}
                    disabled={busyId === module.id || query.trim() !== '' || position < 0 || position >= ordered.length - 1}
                    aria-label={`Move ${module.title || module.name} down`}
                    title="Move down"
                    className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30"
                  >
                    <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
              <button
                ref={(node) => {
                  menuAnchorRefs.current[module.id] = node;
                }}
                type="button"
                onClick={() => setMenuTargetId((current) => (current === module.id ? null : module.id))}
                disabled={busyId === module.id}
                aria-label={`${module.title || module.name} options`}
                className="flex-shrink-0 rounded-md p-1.5 text-slate-500 hover:bg-slate-100 disabled:opacity-40"
              >
                <MoreVertical className="h-4 w-4" />
              </button>
              {menuTargetId === module.id && (
                <LayerOptionsMenu
                  ariaLabel={`${module.title || module.name} options`}
                  anchorRef={{ current: menuAnchorRefs.current[module.id] ?? null }}
                  items={menuItemsFor(module)}
                  onClose={() => setMenuTargetId(null)}
                />
              )}
            </li>
            );
          })}
        </ul>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {filtered.map((module) => (
            <div key={module.id} className="relative overflow-hidden rounded-lg border border-slate-200 bg-white">
              <button type="button" onClick={() => setSelectedId(module.id)} className="block w-full text-left">
                <Thumbnail module={module} className="h-20 w-full" />
                <div className="p-2.5">
                  <p className="truncate text-[12.5px] font-medium text-slate-800">
                    {module.title || module.name}
                  </p>
                  {badges(module)}
                </div>
              </button>
              <button
                ref={(node) => {
                  menuAnchorRefs.current[module.id] = node;
                }}
                type="button"
                onClick={() => setMenuTargetId((current) => (current === module.id ? null : module.id))}
                disabled={busyId === module.id}
                aria-label={`${module.title || module.name} options`}
                className="absolute right-1.5 top-1.5 rounded-md bg-white/90 p-1.5 text-slate-600 shadow-sm hover:bg-white disabled:opacity-40"
              >
                <MoreVertical className="h-4 w-4" />
              </button>
              {menuTargetId === module.id && (
                <LayerOptionsMenu
                  ariaLabel={`${module.title || module.name} options`}
                  anchorRef={{ current: menuAnchorRefs.current[module.id] ?? null }}
                  items={menuItemsFor(module)}
                  onClose={() => setMenuTargetId(null)}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Share… above already offers a "Share with a group" checklist, but a
          group's own Applications list (User & Access › User Groups) remains
          a second way to reach the same grant — useful for handing a group
          several apps at once rather than opening each app's own dialog. */}
      {(isAdmin || canShareModules) && (
        <p className="text-[10.5px] text-slate-400">
          You can also grant a whole group several applications at once from the group's own page in
          Settings → User & Access → User Groups.
        </p>
      )}
    </div>
  );
}
